import type {
  BattleSummary,
  LaneMomentum,
  LaneRiskLevel,
  LaneStateSummary,
  LaneTerrain,
  ObjectiveStateSummary,
  PlayerUnitRole,
  PriorityTowerSummary
} from "../../../shared/types/BattleEvent";
import type { CastleSegmentId, CastleStateSummary } from "../../../shared/types/BattleEvent";
import type { LaneNumber } from "../../../shared/types/CommanderDecision";
import { Unit } from "../game/entities/Unit";
import { Tower } from "../game/entities/Tower";
import mapConfig from "../game/data/mapConfig.json";

const lanes: LaneNumber[] = [1, 2, 3, 4, 5];

function defenseLevel(count: number): LaneStateSummary["playerDefenseLevel"] {
  if (count <= 0) return "none";
  if (count === 1) return "low";
  if (count === 2) return "medium";
  if (count <= 4) return "high";
  return "very_high";
}

function weightedDefenseLevel(towerCount: number, unitCount: number): LaneStateSummary["playerDefenseLevel"] {
  return defenseLevel(towerCount + Math.ceil(unitCount * 0.6));
}

function pressureLevel(count: number): LaneStateSummary["enemyPressureLevel"] {
  if (count <= 0) return "none";
  if (count === 1) return "low";
  if (count === 2) return "medium";
  if (count <= 4) return "high";
  return "critical";
}

function openAdjacentLanes(lane: LaneNumber): LaneNumber[] {
  return (mapConfig.laneCrossings ?? [])
    .filter((crossing) => crossing.enabled && (crossing.fromLane === lane || crossing.toLane === lane))
    .map((crossing) => (crossing.fromLane === lane ? crossing.toLane : crossing.fromLane) as LaneNumber)
    .filter((value, index, all) => all.indexOf(value) === index)
    .sort((a, b) => a - b);
}

function laneTerrain(lane: LaneNumber): LaneTerrain | undefined {
  return mapConfig.lanes.find((item) => item.lane === lane)?.terrain as LaneTerrain | undefined;
}

function recentSummonsForLane(actions: string[], lane: LaneNumber) {
  return actions.filter((action) => action.startsWith("summoned_") && action.endsWith(`_lane_${lane}`)).length;
}

function playerThreatToAiCastle(nearestPlayerX?: number) {
  if (nearestPlayerX === undefined) return 0;
  const travelFromSpawn = Math.max(0, mapConfig.playerSpawnX - nearestPlayerX);
  const baseThreat = Math.floor(travelFromSpawn / 145);
  const breachBonus = nearestPlayerX <= 420 ? 2 : nearestPlayerX <= 620 ? 1 : 0;
  return Math.max(0, Math.min(6, baseThreat + breachBonus));
}

function projectedBreachTimeSec(units: Unit[]) {
  const candidates = units
    .filter((unit) => unit.damage > 0 && unit.x > mapConfig.aiFortressX + 92)
    .map((unit) => {
      const speed = Math.max(12, unit.baseSpeed * unit.slowMultiplier);
      return (unit.x - (mapConfig.aiFortressX + 92)) / speed;
    })
    .filter((value) => Number.isFinite(value) && value >= 0);
  if (candidates.length === 0) return undefined;
  return Math.max(1, Math.round(Math.min(...candidates)));
}

function projectedPlayerDps(units: Unit[]) {
  const dps = units
    .filter((unit) => unit.damage > 0)
    .reduce((total, unit) => total + unit.damage / Math.max(0.25, unit.attackRateMs / 1000), 0);
  return dps > 0 ? Math.round(dps * 10) / 10 : undefined;
}

function enemyResponseScore(enemyCount: number, forwardEnemyCount: number, nearestEnemyX?: number) {
  const castlePressureBonus = nearestEnemyX !== undefined && nearestEnemyX >= 900 ? 1 : 0;
  return Math.round(enemyCount + forwardEnemyCount * 1.15 + castlePressureBonus);
}

function laneMomentum(playerCommitmentScore: number, responseScore: number): LaneMomentum {
  if (playerCommitmentScore >= responseScore + 2 && playerCommitmentScore >= 4) return "player_push";
  if (responseScore >= playerCommitmentScore + 2 && responseScore >= 3) return "enemy_push";
  if (playerCommitmentScore > 0 || responseScore > 0) return "contested";
  return "quiet";
}

function clampMorale(value: number) {
  return Math.max(0, Math.min(100, Math.round(value)));
}

function moraleCountDelta(value: number) {
  return Math.max(-12, Math.min(12, value * 3));
}

function moraleState(value: number): NonNullable<LaneStateSummary["playerMoraleState"]> {
  if (value < 30) return "broken";
  if (value < 50) return "shaken";
  if (value >= 80) return "inspired";
  return "steady";
}

function isEncircledBy(unit: Unit, opponents: Unit[]) {
  const nearby = opponents.filter((opponent) => opponent.alive && opponent.lane === unit.lane && Math.abs(opponent.x - unit.x) <= 138);
  if (nearby.length < 2) return false;
  return nearby.some((opponent) => opponent.x < unit.x - 18) && nearby.some((opponent) => opponent.x > unit.x + 18);
}

function playerRoleCounts(units: Unit[]) {
  const counts: Partial<Record<PlayerUnitRole, number>> = {};
  for (const unit of units) {
    const role = unit.role as PlayerUnitRole;
    counts[role] = (counts[role] ?? 0) + 1;
  }
  return counts;
}

function priorityTowerReason(tower: Tower): PriorityTowerSummary["reason"] {
  if (tower.blocker) return "wall_block";
  if (tower.healing || tower.scoutCoverage) return "support_aura";
  if (tower.splashRadius > 0) return "splash_dps";
  if (tower.range >= 230) return "long_range";
  return "raw_dps";
}

function towerThreatScore(tower: Tower) {
  const dps = Math.max(0, tower.damage) / Math.max(0.25, tower.attackRateMs / 1000);
  const healingPressure = tower.healing ? Math.abs(tower.damage) / Math.max(0.25, tower.attackRateMs / 1000) * 1.55 : 0;
  const splashBonus = tower.splashRadius > 0 ? 14 + tower.splashRadius / 12 : 0;
  const slowBonus = tower.slow > 0 ? 8 + tower.slow * 16 : 0;
  const rangeBonus = Math.max(0, tower.range - 165) / 12;
  const blockerBonus = tower.blocker ? 18 + Math.min(10, tower.maxHp / 80) : 0;
  const scoutBonus = tower.scoutCoverage ? 5 : 0;
  const healthBonus = Math.min(8, tower.hp / Math.max(1, tower.maxHp) * 8);
  return Math.round((dps + healingPressure + splashBonus + slowBonus + rangeBonus + blockerBonus + scoutBonus + healthBonus) * 10) / 10;
}

function priorityTowers(towers: Tower[]): PriorityTowerSummary[] {
  return towers
    .filter((tower) => !tower.destroyed && !tower.consumed)
    .map((tower) => ({
      lane: tower.lane,
      towerId: tower.id,
      x: Math.round(tower.x),
      threatScore: towerThreatScore(tower),
      reason: priorityTowerReason(tower)
    }))
    .filter((tower) => tower.threatScore >= 24)
    .sort((a, b) => b.threatScore - a.threatScore || a.x - b.x)
    .slice(0, 3);
}

function laneRisk(input: {
  enemyCount: number;
  forwardEnemyUnits: number;
  towerCount: number;
  playerUnitCount: number;
  recentDamage: number;
  nearestEnemyToPlayerCastleX?: number;
  playerEncircledUnits: number;
  enemyEncircledUnits: number;
}): { riskLevel: LaneRiskLevel; riskReasons: string[] } {
  let score = 0;
  const reasons: string[] = [];

  if (input.enemyCount > 0) {
    score += input.enemyCount;
    reasons.push("enemy_units");
  }
  if (input.forwardEnemyUnits > 0) {
    score += input.forwardEnemyUnits * 2;
    reasons.push("forward_enemies");
  }
  if (input.nearestEnemyToPlayerCastleX !== undefined && input.nearestEnemyToPlayerCastleX >= 910) {
    score += 4;
    reasons.push("castle_near");
  } else if (input.nearestEnemyToPlayerCastleX !== undefined && input.nearestEnemyToPlayerCastleX >= 760) {
    score += 2;
    reasons.push("enemy_advanced");
  }
  if (input.recentDamage > 0) {
    score += Math.min(5, Math.ceil(input.recentDamage / 45));
    reasons.push("recent_castle_damage");
  }
  if (input.playerEncircledUnits > 0) {
    score += input.playerEncircledUnits * 2;
    reasons.push("player_encircled");
  }
  if (input.towerCount === 0 && input.playerUnitCount <= 1 && input.enemyCount > 0) {
    score += 2;
    reasons.push("thin_defense");
  }
  if (input.enemyEncircledUnits > 0) {
    score -= Math.min(3, input.enemyEncircledUnits);
    reasons.push("enemy_encircled");
  }

  if (score >= 11) return { riskLevel: "collapse", riskReasons: reasons.slice(0, 3) };
  if (score >= 7) return { riskLevel: "danger", riskReasons: reasons.slice(0, 3) };
  if (score >= 4) return { riskLevel: "contested", riskReasons: reasons.slice(0, 3) };
  if (score >= 1) return { riskLevel: "watch", riskReasons: reasons.slice(0, 3) };
  return { riskLevel: "safe", riskReasons: ["stable"] };
}

export function createBattleSummary(input: {
  battleTime: number;
  playerCastleHp: number;
  aiCastleHp: number;
  playerCastleSegments?: CastleStateSummary;
  aiCastleSegments?: CastleStateSummary;
  counterGauge: number;
  commanderUltimate?: BattleSummary["commanderUltimate"];
  playerGold: number;
  activePlayerUnits?: number;
  upkeepPenaltyPercent?: number;
  effectiveIncomePerSecond?: number;
  economyInvestmentIncomePerSecond?: number;
  economyInvestments?: BattleSummary["economyInvestments"];
  playerTechState?: BattleSummary["playerTechState"];
  activeVeteranUnits?: number;
  activePlayerAbilities?: BattleSummary["activePlayerAbilities"];
  aiBudget?: BattleSummary["aiBudget"];
  playerEconomyOutposts?: number;
  enemyEconomyOutposts?: number;
  economicOutposts?: BattleSummary["economicOutposts"];
  crossingControls?: BattleSummary["crossingControls"];
  laneMoraleByLane?: Partial<Record<LaneNumber, { player: number; enemy: number }>>;
  laneCollapseByLane?: Partial<Record<LaneNumber, { active: boolean; remainingSec: number; penalty: number }>>;
  recentCastleRepairs?: Partial<Record<CastleSegmentId, number>>;
  queuedPlayerUnitsByLane?: Partial<Record<LaneNumber, number>>;
  objectivePoints?: number;
  activeObjectives?: string[];
  activeObjectiveDetails?: ObjectiveStateSummary[];
  destroyedTowers?: string[];
  upgradePatterns?: string[];
  objectiveSuccessRate?: number;
  recentCastleDamageByLane?: Partial<Record<LaneNumber, number>>;
  pressuredPlayerCastleSegment?: CastleStateSummary[keyof CastleStateSummary]["id"];
  towers: Tower[];
  playerUnits?: Unit[];
  enemies: Unit[];
  recentActions: string[];
}): BattleSummary {
  const activePriorityTowers = priorityTowers(input.towers);
  const laneStates = lanes.map((lane) => {
    const laneTowers = input.towers.filter((tower) => tower.lane === lane && !tower.destroyed && !tower.consumed);
    const lanePriorityTowers = activePriorityTowers.filter((tower) => tower.lane === lane);
    const lanePlayerUnits = input.playerUnits?.filter((unit) => unit.alive && unit.lane === lane) ?? [];
    const laneEnemies = input.enemies.filter((enemy) => enemy.lane === lane);
    const forwardPlayerUnits = lanePlayerUnits.filter((unit) => unit.x < 610).length;
    const forwardEnemyUnits = laneEnemies.filter((enemy) => enemy.x > 670).length;
    const nearestPlayerToAiCastleX = lanePlayerUnits.sort((a, b) => a.x - b.x)[0]?.x;
    const nearestEnemyToPlayerCastleX = laneEnemies.sort((a, b) => b.x - a.x)[0]?.x;
    const projectedBreach = projectedBreachTimeSec(lanePlayerUnits);
    const projectedDps = projectedPlayerDps(lanePlayerUnits);
    const aiCastleThreat = playerThreatToAiCastle(nearestPlayerToAiCastleX);
    const recentSummons = recentSummonsForLane(input.recentActions, lane);
    const queuedUnits = input.queuedPlayerUnitsByLane?.[lane] ?? 0;
    const playerEncircledUnits = lanePlayerUnits.filter((unit) => isEncircledBy(unit, laneEnemies)).length;
    const enemyEncircledUnits = laneEnemies.filter((enemy) => isEncircledBy(enemy, lanePlayerUnits)).length;
    const playerVeteranUnits = lanePlayerUnits.filter((unit) => unit.isVeteran).length;
    const roleCounts = playerRoleCounts(lanePlayerUnits);
    const economyOutpost = input.economicOutposts?.find((outpost) => outpost.lane === lane);
    const fallbackPlayerMorale = clampMorale(
      60 +
        moraleCountDelta(lanePlayerUnits.length - laneEnemies.length) -
        Math.min(14, (input.recentCastleDamageByLane?.[lane] ?? 0) / 40) -
        playerEncircledUnits * 18 +
        enemyEncircledUnits * 8 +
        (economyOutpost?.owner === "player" ? 4 : economyOutpost?.owner === "enemy" ? -4 : 0)
    );
    const fallbackEnemyMorale = clampMorale(
      60 +
        moraleCountDelta(laneEnemies.length - lanePlayerUnits.length) -
        enemyEncircledUnits * 18 +
        playerEncircledUnits * 8 -
        aiCastleThreat * 2 -
        (projectedBreach !== undefined && projectedBreach <= 18 ? 8 : 0) +
        (economyOutpost?.owner === "enemy" ? 4 : economyOutpost?.owner === "player" ? -4 : 0)
    );
    const playerMorale = input.laneMoraleByLane?.[lane]?.player ?? fallbackPlayerMorale;
    const enemyMorale = input.laneMoraleByLane?.[lane]?.enemy ?? fallbackEnemyMorale;
    const laneCollapse = input.laneCollapseByLane?.[lane];
    const commitmentScore = Math.round(lanePlayerUnits.length + forwardPlayerUnits * 1.25 + playerVeteranUnits * 1.5 + aiCastleThreat + recentSummons * 0.75 + queuedUnits * 0.9);
    const responseScore = enemyResponseScore(laneEnemies.length, forwardEnemyUnits, nearestEnemyToPlayerCastleX);
    const risk = laneRisk({
      enemyCount: laneEnemies.length,
      forwardEnemyUnits,
      towerCount: laneTowers.length,
      playerUnitCount: lanePlayerUnits.length,
      recentDamage: input.recentCastleDamageByLane?.[lane] ?? 0,
      nearestEnemyToPlayerCastleX,
      playerEncircledUnits,
      enemyEncircledUnits
    });
    return {
      lane,
      terrain: laneTerrain(lane),
      playerDefenseLevel: weightedDefenseLevel(laneTowers.length, lanePlayerUnits.length),
      enemyPressureLevel: pressureLevel(laneEnemies.length),
      hasScoutCoverage: laneTowers.some((tower) => tower.scoutCoverage),
      recentDamageToCastle: input.recentCastleDamageByLane?.[lane] ?? 0,
      playerUnitCount: lanePlayerUnits.length,
      playerRoleCounts: roleCounts,
      enemyUnitCount: laneEnemies.length,
      towerCount: laneTowers.length,
      forwardPlayerUnits,
      forwardEnemyUnits,
      nearestPlayerToAiCastleX,
      nearestEnemyToPlayerCastleX,
      projectedBreachTimeSec: projectedBreach,
      projectedPlayerDps: projectedDps,
      playerCommitmentScore: commitmentScore,
      playerThreatToAiCastle: aiCastleThreat,
      enemyResponseScore: responseScore,
      laneMomentum: laneMomentum(commitmentScore, responseScore),
      riskLevel: risk.riskLevel,
      riskReasons: risk.riskReasons,
      playerEncircledUnits,
      enemyEncircledUnits,
      playerMorale,
      enemyMorale,
      playerMoraleState: moraleState(playerMorale),
      enemyMoraleState: moraleState(enemyMorale),
      laneCollapseActive: laneCollapse?.active,
      laneCollapseRemainingSec: laneCollapse?.remainingSec,
      laneCollapsePenalty: laneCollapse?.penalty,
      playerVeteranUnits,
      openAdjacentLanes: openAdjacentLanes(lane),
      economyOutpostOwner: economyOutpost?.owner,
      economyOutpostProgress: economyOutpost?.progress,
      dominantPlayerTower: lanePriorityTowers[0]?.towerId ?? laneTowers[0]?.id,
      dominantEnemyType: laneEnemies[0]?.id
    };
  });

  const detectedPatterns = laneStates
    .filter((state) => state.playerDefenseLevel === "very_high")
    .map((state) => (state.lane === 3 ? "player_overdefends_mid_lane" : `player_overdefends_lane_${state.lane}`));
  const pressureThreats = laneStates
    .filter((state) => state.recentDamageToCastle > 0)
    .map((state) => `castle_damage_lane_${state.lane}`);
  const unitOvercommitPatterns = laneStates
    .filter((state) => (state.playerUnitCount ?? 0) >= 4)
    .map((state) => `player_mass_units_lane_${state.lane}`);
  const playerPushPatterns = laneStates
    .filter((state) => state.laneMomentum === "player_push")
    .map((state) => `player_push_lane_${state.lane}`);
  const objectivePatterns = (input.activeObjectiveDetails ?? [])
    .filter((objective) => objective.riskLevel === "high" && objective.captureProgress >= 0.35)
    .map((objective) => `player_contesting_high_risk_objective_lane_${objective.lane}`);
  const economyPatterns = (input.economicOutposts ?? [])
    .filter((outpost) => outpost.owner === "player")
    .map((outpost) => `player_controls_economy_outpost_lane_${outpost.lane}`);
  const economyInvestmentPatterns = (input.economyInvestments ?? [])
    .filter((investment) => investment.active)
    .map((investment) => `player_has_economy_investment_lane_${investment.lane}`);
  const techPatterns = (input.playerTechState?.researched ?? []).map((tech) => `player_tech_${tech}`);
  const veteranPatterns = laneStates
    .filter((state) => (state.playerVeteranUnits ?? 0) > 0)
    .map((state) => `player_veterans_lane_${state.lane}`);
  const rolePatterns = laneStates.flatMap((state) =>
    Object.entries(state.playerRoleCounts ?? {})
      .filter(([, count]) => (count ?? 0) >= 2)
      .map(([role]) => `player_role_${role}_lane_${state.lane}`)
  );
  const priorityTowerPatterns = activePriorityTowers.map((tower) => `priority_tower_${tower.towerId}_lane_${tower.lane}`);
  const recentSummonCount = input.recentActions.filter((action) => action.startsWith("summoned_") || action.startsWith("queued_")).length;
  const bankedRushLane = [...laneStates]
    .sort((a, b) =>
      (b.playerCommitmentScore ?? 0) - (a.playerCommitmentScore ?? 0) ||
      (b.playerUnitCount ?? 0) - (a.playerUnitCount ?? 0) ||
      (a.projectedBreachTimeSec ?? 999) - (b.projectedBreachTimeSec ?? 999)
    )[0]?.lane;
  const bankedRushRisk =
    input.playerGold >= 420 && recentSummonCount <= 1
      ? "high"
      : input.playerGold >= 280 && recentSummonCount <= 2
        ? "watch"
        : "none";
  const projectedThreats = laneStates
    .filter((state) => (state.projectedBreachTimeSec ?? 999) <= 18)
    .map((state) => `projected_breach_lane_${state.lane}_${state.projectedBreachTimeSec}s`);
  const collapseThreats = laneStates
    .filter((state) => state.laneCollapseActive)
    .map((state) => `lane_collapse_active_lane_${state.lane}`);
  const activeAbilityPatterns = (input.activePlayerAbilities ?? [])
    .filter((ability) => ability.active)
    .map((ability) => `player_ability_${ability.abilityId}_lane_${ability.lane}`);

  return {
    battleTime: input.battleTime,
    playerCastleHp: input.playerCastleHp,
    aiCastleHp: input.aiCastleHp,
    playerCastleSegments: input.playerCastleSegments,
    aiCastleSegments: input.aiCastleSegments,
    weakestPlayerCastleSegment: input.playerCastleSegments ? weakestSegment(input.playerCastleSegments) : undefined,
    weakestAiCastleSegment: input.aiCastleSegments ? weakestSegment(input.aiCastleSegments) : undefined,
    pressuredPlayerCastleSegment: input.pressuredPlayerCastleSegment,
    counterGauge: input.counterGauge,
    commanderUltimate: input.commanderUltimate,
    playerGold: input.playerGold,
    activePlayerUnits: input.activePlayerUnits,
    upkeepPenaltyPercent: input.upkeepPenaltyPercent,
    effectiveIncomePerSecond: input.effectiveIncomePerSecond,
    economyInvestmentIncomePerSecond: input.economyInvestmentIncomePerSecond,
    economyInvestments: input.economyInvestments,
    playerTechState: input.playerTechState,
    activeVeteranUnits: input.activeVeteranUnits,
    activePlayerAbilities: input.activePlayerAbilities,
    aiBudget: input.aiBudget,
    priorityTowers: activePriorityTowers,
    bankedRushRisk,
    bankedRushLane: bankedRushRisk === "none" ? undefined : bankedRushLane,
    playerEconomyOutposts: input.playerEconomyOutposts,
    enemyEconomyOutposts: input.enemyEconomyOutposts,
    economicOutposts: input.economicOutposts,
    crossingControls: input.crossingControls,
    recentCastleRepairs: input.recentCastleRepairs,
    queuedPlayerUnitsByLane: input.queuedPlayerUnitsByLane,
    objectivePoints: input.objectivePoints,
    activeObjectives: input.activeObjectives,
    activeObjectiveDetails: input.activeObjectiveDetails,
    destroyedTowers: input.destroyedTowers,
    upgradePatterns: input.upgradePatterns?.slice(-10),
    objectiveSuccessRate: input.objectiveSuccessRate,
    laneStates,
    recentPlayerActions: input.recentActions.slice(-10),
    detectedPatterns: [
      ...detectedPatterns,
      ...unitOvercommitPatterns,
      ...playerPushPatterns,
      ...objectivePatterns,
      ...economyPatterns,
      ...economyInvestmentPatterns,
      ...techPatterns,
      ...veteranPatterns,
      ...activeAbilityPatterns,
      ...rolePatterns,
      ...priorityTowerPatterns,
      ...(bankedRushRisk !== "none" ? [`player_banked_rush_${bankedRushRisk}`] : [])
    ],
    currentThreats: [
      ...pressureThreats,
      ...projectedThreats,
      ...collapseThreats,
      ...activeAbilityPatterns,
      ...activePriorityTowers.map((tower) => `priority_tower_lane_${tower.lane}_${tower.reason}`),
      ...(input.activeObjectiveDetails ?? [])
        .filter((objective) => objective.captureProgress >= 0.5)
        .map((objective) => `objective_capture_lane_${objective.lane}`),
      ...laneStates.filter((state) => state.enemyPressureLevel === "high" || state.enemyPressureLevel === "critical").map((state) => `lane_${state.lane}`)
    ]
  };
}

function weakestSegment(segments: CastleStateSummary) {
  return ([segments.top, segments.main, segments.bottom] as const)
    .filter((segment) => !segment.destroyed)
    .sort((a, b) => a.hp / a.maxHp - b.hp / b.maxHp)[0]?.id;
}
